import type { AppData, Report } from '../domain.js';

const report = (
  id: string,
  headline: string,
  names: [string, string, string, string],
  probabilities: [number, number, number, number],
  createdAt = '2026-07-13T08:40:00+09:00',
  inputVersion = 'sample-schedule-003+sample-records-007',
): Report => ({
  id,
  createdAt,
  inputVersion,
  methodVersion: 'baseline-score-v0.1',
  sampleRange: '최근 5회 출전 기록과 기수 최근 30일 기록',
  headline,
  metricMode: 'score',
  probabilities: [],
  scores: names.map((horseName, index) => ({
    gate: index + 1,
    horseName,
    overall: Math.min(100, probabilities[index] * 2 + 18),
    recentForm: Math.min(100, probabilities[index] * 2 + 12 + index),
    distanceFit: Math.min(100, probabilities[index] * 2 + 8 + index * 2),
    market: null,
    sampleCount: 3,
  })),
  perspectives: [
    {
      type: 'leading',
      title: '우세도 분석',
      summary: '현재 지표가 고르게 나타난 말을 먼저 비교해요.',
      candidates: names.slice(0, 2).map((horseName, index) => ({
        gate: index + 1,
        horseName,
        role: index === 0 ? '우세도 상단' : '비교 후보',
        reasons: ['최근 기록과 거리 경험을 함께 반영했어요.'],
        cautions: ['샘플 화면용 분석이며 실제 경주 판단에 사용할 수 없어요.'],
      })),
    },
    {
      type: 'variable',
      title: '변수 포함 분석',
      summary: '상단 지표와 다른 흐름을 보이는 대상을 확인해요.',
      candidates: [{
        gate: 3,
        horseName: names[2],
        role: '변수 후보',
        reasons: ['최근 기록의 변동 폭을 따로 확인했어요.'],
        cautions: ['당일 상태는 반영하지 않아요.'],
      }],
    },
    {
      type: 'upset',
      title: '이변 요인 분석',
      summary: '표본은 약하지만 확인할 근거가 있는 대상을 살펴봐요.',
      candidates: [{
        gate: 4,
        horseName: names[3],
        role: '관찰 후보',
        reasons: ['부담중량과 최근 흐름을 함께 비교했어요.'],
        cautions: ['표본이 적어 불확실성이 커요.'],
      }],
    },
  ],
  signals: [
    { title: '초반 전개', detail: '최근 S1F 기록이 출전 구성의 중앙값보다 안정적인 편이에요.', tone: 'positive' },
    { title: '거리 적합성', detail: '같은 거리 표본이 적어 변동 폭을 넓게 봐야 해요.', tone: 'watch' },
    { title: '기수 조합', detail: '최근 조합 기록과 기수 단독 기록을 분리해 반영했어요.', tone: 'neutral' },
  ],
  limitations: [
    '점수는 공공데이터에 포함된 과거 기록만 이용한 MVP 상대 비교값이에요.',
    '당일 컨디션, 돌발 상황, 실시간 배당 변화는 반영하지 않아요.',
    '경기 결과를 보장하거나 금전적 행동을 권유하지 않아요.',
  ],
});

export const demoData: AppData = {
  meta: {
    schemaVersion: '1.0.0',
    bundleVersion: 'sample-bundle-009',
    publishedAt: '2026-07-13T10:30:00+09:00',
    isSample: true,
    notice: '화면 검증용 샘플 데이터예요. 실제 경주 정보가 아니에요.',
    schedule: {
      label: '경주계획·출전표',
      version: 'sample-schedule-003',
      sourceCheckedAt: '2026-07-13T10:10:00+09:00',
      publishedAt: '2026-07-13T10:30:00+09:00',
      latestRaceDate: '2026-07-19',
      state: 'ready',
    },
    records: {
      label: '결과·구간기록·배당',
      version: 'sample-records-007',
      sourceCheckedAt: '2026-07-13T10:15:00+09:00',
      publishedAt: '2026-07-13T10:30:00+09:00',
      latestRaceDate: '2026-07-12',
      state: 'ready',
    },
    jockeys: {
      label: '기수 정보',
      version: 'sample-jockeys-002',
      sourceCheckedAt: '2026-07-13T10:18:00+09:00',
      publishedAt: '2026-07-13T10:30:00+09:00',
      latestRaceDate: null,
      state: 'ready',
    },
    sources: [
      { name: '전국 경마공원 경주계획표', path: 'API72_2/racePlan_2', purpose: '예정 경주' },
      { name: '출전표 정보', path: 'API26_2/entrySheet_2', purpose: '출전 말·기수' },
      { name: '경주기록 정보', path: 'API4_3', purpose: '경주 결과 요약' },
      { name: '경주성적정보', path: 'API214_1/RaceDetailResult_1', purpose: '구간별 상세 기록' },
      { name: '확정배당율 통합 정보', path: 'API160_1/integratedInfo_1', purpose: '확정 배당' },
      { name: '기수 정보', path: 'API12_1/jockeyInfo_1', purpose: '기수 통산 기록' },
    ],
  },
  horses: [
    {
      id: 'horse-spring', name: '새봄질주', country: '한국', sex: '암', age: 4, rating: 58, trainer: '김도현',
      recentRuns: [
        { date: '2026-06-21', meet: '서울', distance: 1200, rank: 2, runners: 10, record: '1:14.2' },
        { date: '2026-05-24', meet: '서울', distance: 1200, rank: 1, runners: 11, record: '1:13.9' },
        { date: '2026-04-19', meet: '서울', distance: 1300, rank: 5, runners: 12, record: '1:21.0' },
      ],
    },
    {
      id: 'horse-paddock', name: '패덕스타', country: '한국', sex: '수', age: 5, rating: 61, trainer: '이주원',
      recentRuns: [
        { date: '2026-06-28', meet: '서울', distance: 1200, rank: 1, runners: 9, record: '1:13.8' },
        { date: '2026-05-31', meet: '서울', distance: 1400, rank: 4, runners: 11, record: '1:27.0' },
        { date: '2026-04-26', meet: '서울', distance: 1200, rank: 3, runners: 12, record: '1:14.6' },
      ],
    },
    {
      id: 'horse-cloud', name: '구름발', country: '한국', sex: '거', age: 4, rating: 55, trainer: '박현수',
      recentRuns: [
        { date: '2026-06-14', meet: '부산경남', distance: 1400, rank: 3, runners: 12, record: '1:27.5' },
        { date: '2026-05-17', meet: '부산경남', distance: 1300, rank: 6, runners: 11, record: '1:21.8' },
        { date: '2026-04-12', meet: '서울', distance: 1200, rank: 2, runners: 10, record: '1:14.3' },
      ],
    },
    {
      id: 'horse-night', name: '밤빛', country: '한국', sex: '암', age: 3, rating: 52, trainer: '최민재',
      recentRuns: [
        { date: '2026-06-07', meet: '제주', distance: 1000, rank: 1, runners: 10, record: '1:02.8' },
        { date: '2026-05-10', meet: '제주', distance: 1000, rank: 4, runners: 10, record: '1:03.4' },
        { date: '2026-04-05', meet: '제주', distance: 900, rank: null, runners: 9, record: null },
      ],
    },
  ],
  jockeys: [
    {
      id: 'jockey-kim', name: '김하늘', stable: '12조', careerStarts: 814, careerWins: 92, recentTop3Rate: 31.4,
      updatedAt: '2026-07-13T10:18:00+09:00',
      recentRuns: [
        { date: '2026-07-12', meet: '서울', distance: 1200, rank: 1, runners: 11, record: '1:13.7' },
        { date: '2026-07-12', meet: '서울', distance: 1400, rank: 4, runners: 10, record: '1:27.2' },
        { date: '2026-07-05', meet: '서울', distance: 1300, rank: 2, runners: 12, record: '1:20.9' },
      ],
    },
    {
      id: 'jockey-lee', name: '이도윤', stable: '7조', careerStarts: 1022, careerWins: 108, recentTop3Rate: 28.8,
      updatedAt: '2026-07-13T10:18:00+09:00',
      recentRuns: [
        { date: '2026-07-12', meet: '서울', distance: 1200, rank: 2, runners: 11, record: '1:13.9' },
        { date: '2026-07-05', meet: '서울', distance: 1200, rank: 5, runners: 12, record: '1:14.8' },
      ],
    },
    {
      id: 'jockey-park', name: '박서준', stable: '18조', careerStarts: 611, careerWins: 70, recentTop3Rate: 33.1,
      updatedAt: '2026-07-13T10:18:00+09:00',
      recentRuns: [
        { date: '2026-07-12', meet: '부산경남', distance: 1400, rank: 3, runners: 12, record: '1:27.1' },
        { date: '2026-07-11', meet: '부산경남', distance: 1200, rank: 1, runners: 10, record: '1:14.0' },
      ],
    },
    {
      id: 'jockey-choi', name: '최유진', stable: '3조', careerStarts: 429, careerWins: 41, recentTop3Rate: 25.6,
      updatedAt: '2026-07-13T10:18:00+09:00',
      recentRuns: [
        { date: '2026-07-12', meet: '제주', distance: 1000, rank: 4, runners: 10, record: '1:03.2' },
        { date: '2026-07-11', meet: '제주', distance: 1100, rank: 2, runners: 9, record: '1:10.8' },
      ],
    },
  ],
  races: [
    {
      id: 'sample-seoul-20260712-7', meet: '서울', date: '2026-07-12', dayLabel: '일', number: 7, startTime: '16:05',
      status: 'completed', analysisStatus: 'ready', distance: 1200, grade: '국4', title: '샘플 일반경주', ageCondition: '3세 이상', weightCondition: '핸디캡',
      weather: '맑음', track: '건조', prize: 45000000,
      entries: [
        { gate: 1, horseId: 'horse-paddock', horseName: '패덕스타', jockeyId: 'jockey-kim', jockeyName: '김하늘', carriedWeight: 55.5, bodyWeight: 488, bodyWeightChange: 3, rating: 61, equipment: null },
        { gate: 2, horseId: 'horse-spring', horseName: '새봄질주', jockeyId: 'jockey-lee', jockeyName: '이도윤', carriedWeight: 54, bodyWeight: 462, bodyWeightChange: -2, rating: 58, equipment: '눈가면' },
        { gate: 3, horseId: 'horse-cloud', horseName: '구름발', jockeyId: 'jockey-park', jockeyName: '박서준', carriedWeight: 53.5, bodyWeight: 475, bodyWeightChange: 1, rating: 55, equipment: null },
        { gate: 4, horseId: 'horse-night', horseName: '밤빛', jockeyId: 'jockey-choi', jockeyName: '최유진', carriedWeight: 52, bodyWeight: 447, bodyWeightChange: 0, rating: 52, equipment: null },
      ],
      results: [
        { rank: 1, gate: 1, horseId: 'horse-paddock', horseName: '패덕스타', jockeyName: '김하늘', record: '1:13.7', gap: '-', bodyWeight: 488, s1f: '13.4', g3f: '37.8', g1f: '12.9' },
        { rank: 2, gate: 2, horseId: 'horse-spring', horseName: '새봄질주', jockeyName: '이도윤', record: '1:13.9', gap: '1¼', bodyWeight: 462, s1f: '13.6', g3f: '37.7', g1f: '12.8' },
        { rank: 3, gate: 3, horseId: 'horse-cloud', horseName: '구름발', jockeyName: '박서준', record: '1:14.3', gap: '2', bodyWeight: 475, s1f: '13.8', g3f: '38.0', g1f: '13.0' },
        { rank: 4, gate: 4, horseId: 'horse-night', horseName: '밤빛', jockeyName: '최유진', record: '1:14.8', gap: '3', bodyWeight: 447, s1f: '13.5', g3f: '38.6', g1f: '13.4' },
      ],
      odds: [
        { type: 'WIN', label: '단승', combination: '1', dividend: 3.2 },
        { type: 'PLC', label: '연승', combination: '1', dividend: 1.5 },
        { type: 'QNL', label: '복승', combination: '1-2', dividend: 7.8 },
        { type: 'EXA', label: '쌍승', combination: '1-2', dividend: 14.1 },
        { type: 'TLA', label: '삼복승', combination: '1-2-3', dividend: 18.6 },
        { type: 'TRI', label: '삼쌍승', combination: '1-2-3', dividend: 49.2 },
      ],
      report: report(
        'report-seoul-20260712-7',
        '선행 흐름은 1번과 2번이 비슷하고, 중반 유지력에서 차이가 날 가능성이 있어요.',
        ['패덕스타', '새봄질주', '구름발', '밤빛'],
        [31, 28, 23, 18],
        '2026-07-11T18:20:00+09:00',
        'sample-schedule-002+sample-records-006',
      ),
    },
    {
      id: 'sample-seoul-20260718-1', meet: '서울', date: '2026-07-18', dayLabel: '토', number: 1, startTime: '10:35',
      status: 'scheduled', analysisStatus: 'ready', distance: 1200, grade: '국6', title: '샘플 신마경주', ageCondition: '2세', weightCondition: '별정A',
      weather: null, track: null, prize: 30000000,
      entries: [
        { gate: 1, horseId: 'horse-spring', horseName: '새봄질주', jockeyId: 'jockey-kim', jockeyName: '김하늘', carriedWeight: 54, bodyWeight: null, bodyWeightChange: null, rating: 58, equipment: null },
        { gate: 2, horseId: 'horse-cloud', horseName: '구름발', jockeyId: 'jockey-park', jockeyName: '박서준', carriedWeight: 55, bodyWeight: null, bodyWeightChange: null, rating: 55, equipment: null },
        { gate: 3, horseId: 'horse-night', horseName: '밤빛', jockeyId: 'jockey-choi', jockeyName: '최유진', carriedWeight: 53, bodyWeight: null, bodyWeightChange: null, rating: 52, equipment: null },
        { gate: 4, horseId: 'horse-paddock', horseName: '패덕스타', jockeyId: 'jockey-lee', jockeyName: '이도윤', carriedWeight: 56, bodyWeight: null, bodyWeightChange: null, rating: 61, equipment: '망사눈가면' },
      ],
      results: [], odds: [],
      oddsSnapshot: {
        collectedAt: '2026-07-18T07:09:49+09:00',
        state: 'pre_race',
        entries: [
          { type: 'WIN', label: '단승', combination: '1', dividend: 2.5 },
          { type: 'WIN', label: '단승', combination: '4', dividend: 3.8 },
          { type: 'WIN', label: '단승', combination: '3', dividend: 6.2 },
          { type: 'WIN', label: '단승', combination: '2', dividend: 8.5 },
          { type: 'PLC', label: '연승', combination: '1', dividend: 1.3 },
          { type: 'PLC', label: '연승', combination: '4', dividend: 1.5 },
          { type: 'TRI', label: '삼쌍승', combination: '1-4-3', dividend: 38.2 },
          { type: 'TRI', label: '삼쌍승', combination: '4-1-3', dividend: 44.7 },
          { type: 'TRI', label: '삼쌍승', combination: '1-3-4', dividend: 51.3 },
        ],
      },
      report: report('report-seoul-20260718-1', '초반 기록이 안정적인 1번과 거리 경험이 많은 4번이 상단에 있어요.', ['새봄질주', '구름발', '밤빛', '패덕스타'], [29, 24, 18, 29]),
    },
    {
      id: 'sample-jeju-20260718-2', meet: '제주', date: '2026-07-18', dayLabel: '토', number: 2, startTime: '11:10',
      status: 'scheduled', analysisStatus: 'ready', distance: 1000, grade: '제5', title: '샘플 일반경주', ageCondition: '3세 이상', weightCondition: '별정',
      weather: null, track: null, prize: 20000000,
      entries: [
        { gate: 1, horseId: 'horse-night', horseName: '밤빛', jockeyId: 'jockey-choi', jockeyName: '최유진', carriedWeight: 54, bodyWeight: null, bodyWeightChange: null, rating: 52, equipment: null },
        { gate: 2, horseId: 'horse-spring', horseName: '새봄질주', jockeyId: 'jockey-kim', jockeyName: '김하늘', carriedWeight: 55, bodyWeight: null, bodyWeightChange: null, rating: 58, equipment: null },
        { gate: 3, horseId: 'horse-cloud', horseName: '구름발', jockeyId: 'jockey-park', jockeyName: '박서준', carriedWeight: 55, bodyWeight: null, bodyWeightChange: null, rating: 55, equipment: null },
        { gate: 4, horseId: 'horse-paddock', horseName: '패덕스타', jockeyId: 'jockey-lee', jockeyName: '이도윤', carriedWeight: 56, bodyWeight: null, bodyWeightChange: null, rating: 61, equipment: null },
      ],
      results: [], odds: [],
      report: report('report-jeju-20260718-2', '짧은 거리 경험이 있는 1번과 최근 출발 기록이 안정적인 2번을 함께 봐야 해요.', ['밤빛', '새봄질주', '구름발', '패덕스타'], [30, 28, 23, 19]),
    },
    {
      id: 'sample-busan-20260718-3', meet: '부산경남', date: '2026-07-18', dayLabel: '토', number: 3, startTime: '12:15',
      status: 'scheduled', analysisStatus: 'ready', distance: 1400, grade: '국5', title: '샘플 일반경주', ageCondition: '3세 이상', weightCondition: '핸디캡',
      weather: null, track: null, prize: 35000000,
      entries: [
        { gate: 1, horseId: 'horse-cloud', horseName: '구름발', jockeyId: 'jockey-park', jockeyName: '박서준', carriedWeight: 54.5, bodyWeight: null, bodyWeightChange: null, rating: 55, equipment: null },
        { gate: 2, horseId: 'horse-paddock', horseName: '패덕스타', jockeyId: 'jockey-kim', jockeyName: '김하늘', carriedWeight: 57, bodyWeight: null, bodyWeightChange: null, rating: 61, equipment: null },
        { gate: 3, horseId: 'horse-spring', horseName: '새봄질주', jockeyId: 'jockey-lee', jockeyName: '이도윤', carriedWeight: 55, bodyWeight: null, bodyWeightChange: null, rating: 58, equipment: null },
        { gate: 4, horseId: 'horse-night', horseName: '밤빛', jockeyId: 'jockey-choi', jockeyName: '최유진', carriedWeight: 52, bodyWeight: null, bodyWeightChange: null, rating: 52, equipment: null },
      ],
      results: [], odds: [],
      report: report('report-busan-20260718-3', '1400m 경험이 있는 1번과 부담중량이 낮은 4번의 차이를 함께 확인해야 해요.', ['구름발', '패덕스타', '새봄질주', '밤빛'], [31, 27, 24, 18]),
    },
    {
      id: 'sample-busan-20260719-3', meet: '부산경남', date: '2026-07-19', dayLabel: '일', number: 3, startTime: '12:15',
      status: 'scheduled', analysisStatus: 'ready', distance: 1400, grade: '국5', title: '샘플 일반경주', ageCondition: '3세 이상', weightCondition: '핸디캡',
      weather: null, track: null, prize: 35000000,
      entries: [
        { gate: 1, horseId: 'horse-cloud', horseName: '구름발', jockeyId: 'jockey-park', jockeyName: '박서준', carriedWeight: 54.5, bodyWeight: null, bodyWeightChange: null, rating: 55, equipment: null },
        { gate: 2, horseId: 'horse-paddock', horseName: '패덕스타', jockeyId: 'jockey-kim', jockeyName: '김하늘', carriedWeight: 57, bodyWeight: null, bodyWeightChange: null, rating: 61, equipment: null },
        { gate: 3, horseId: 'horse-spring', horseName: '새봄질주', jockeyId: 'jockey-lee', jockeyName: '이도윤', carriedWeight: 55, bodyWeight: null, bodyWeightChange: null, rating: 58, equipment: null },
        { gate: 4, horseId: 'horse-night', horseName: '밤빛', jockeyId: 'jockey-choi', jockeyName: '최유진', carriedWeight: 52, bodyWeight: null, bodyWeightChange: null, rating: 52, equipment: null },
      ],
      results: [], odds: [],
      report: report('report-busan-20260719-3', '1400m 경험이 있는 1번이 안정적이지만 부담중량 차이를 함께 봐야 해요.', ['구름발', '패덕스타', '새봄질주', '밤빛'], [32, 27, 25, 16]),
    },
    {
      id: 'sample-jeju-20260713-2', meet: '제주', date: '2026-07-13', dayLabel: '월', number: 2, startTime: '11:20',
      status: 'awaiting_result', analysisStatus: 'ready', distance: 1000, grade: '제5', title: '샘플 일반경주', ageCondition: '3세 이상', weightCondition: '별정',
      weather: null, track: null, prize: 20000000,
      entries: [
        { gate: 1, horseId: 'horse-night', horseName: '밤빛', jockeyId: 'jockey-choi', jockeyName: '최유진', carriedWeight: 54, bodyWeight: null, bodyWeightChange: null, rating: 52, equipment: null },
        { gate: 2, horseId: 'horse-spring', horseName: '새봄질주', jockeyId: 'jockey-kim', jockeyName: '김하늘', carriedWeight: 55, bodyWeight: null, bodyWeightChange: null, rating: 58, equipment: null },
        { gate: 3, horseId: 'horse-cloud', horseName: '구름발', jockeyId: 'jockey-park', jockeyName: '박서준', carriedWeight: 55, bodyWeight: null, bodyWeightChange: null, rating: 55, equipment: null },
        { gate: 4, horseId: 'horse-paddock', horseName: '패덕스타', jockeyId: 'jockey-lee', jockeyName: '이도윤', carriedWeight: 56, bodyWeight: null, bodyWeightChange: null, rating: 61, equipment: null },
      ],
      results: [], odds: [],
      report: report('report-jeju-20260713-2', '짧은 거리 경험이 있는 1번이 앞서지만 표본 수가 적어 불확실성이 커요.', ['밤빛', '새봄질주', '구름발', '패덕스타'], [34, 25, 22, 19]),
    },
  ],
};
